import { z } from 'zod';

// ---------------------------------------------------------------------------
// Price
// ---------------------------------------------------------------------------
export const MoneySchema = z.object({
  amount: z.string(),
  currencyCode: z.string().optional(),
});
export type Money = z.infer<typeof MoneySchema>;

// ---------------------------------------------------------------------------
// Line item variant
// ---------------------------------------------------------------------------
export const LineItemVariantSchema = z.object({
  id: z.string(),
  sku: z.string().optional(),
  price: MoneySchema.optional(),
  title: z.string().optional(),
  availableForSale: z.boolean().optional(),
});
export type LineItemVariant = z.infer<typeof LineItemVariantSchema>;

// ---------------------------------------------------------------------------
// Checkout line item
// ---------------------------------------------------------------------------
export const LineItemSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  quantity: z.number(),
  variant: LineItemVariantSchema.nullable().optional(),
  customAttributes: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
});
export type LineItem = z.infer<typeof LineItemSchema>;

// ---------------------------------------------------------------------------
// Shopify checkout / cart
// ---------------------------------------------------------------------------
export const CheckoutSchema = z.object({
  id: z.string(),
  webUrl: z.string().optional(),
  email: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  lineItems: z.array(LineItemSchema).optional(),
  totalQuantity: z.number().optional(),
  totalPrice: MoneySchema.optional(),
  shippingAddress: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type Checkout = z.infer<typeof CheckoutSchema>;

// ---------------------------------------------------------------------------
// Cart state (what we store in the React Query cache)
// ---------------------------------------------------------------------------
export const CartStateSchema = z.object({
  checkout: CheckoutSchema.nullable(),
  guestCheckout: CheckoutSchema.nullable(),
  /** True after a stale cart is detected and a fresh one is created. */
  createNewCart: z.boolean().optional(),
  /** Set when checkout was recreated + variants added → triggers DB save. */
  updateDB: z.boolean().optional(),
});
export type CartState = z.infer<typeof CartStateSchema>;

// ---------------------------------------------------------------------------
// Shopify shop info
// ---------------------------------------------------------------------------
export const ShopInfoSchema = z.object({
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  primaryDomain: z.object({ url: z.string(), host: z.string() }).optional(),
});
export type ShopInfo = z.infer<typeof ShopInfoSchema>;

// ---------------------------------------------------------------------------
// Shopify product (summary — used in product.fetch)
// ---------------------------------------------------------------------------
export const ShopifyProductSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  variants: z
    .array(
      z.object({
        id: z.string(),
        sku: z.string().optional(),
        price: MoneySchema.optional(),
        availableForSale: z.boolean().optional(),
      }),
    )
    .optional(),
  images: z.array(z.object({ src: z.string() })).optional(),
});
export type ShopifyProduct = z.infer<typeof ShopifyProductSchema>;

// ---------------------------------------------------------------------------
// Mutation payloads
// ---------------------------------------------------------------------------
export const AddVariantPayloadSchema = z.object({
  checkoutId: z.string(),
  lineItemsToAdd: z.array(
    z.object({ variantId: z.string(), quantity: z.number() }),
  ),
  successCallback: z.function().optional(),
});
export type AddVariantPayload = z.infer<typeof AddVariantPayloadSchema>;

export const UpdateQuantityPayloadSchema = z.object({
  lineItemId: z.string(),
  quantity: z.number(),
  checkoutID: z.string(),
});
export type UpdateQuantityPayload = z.infer<typeof UpdateQuantityPayloadSchema>;

export const UpdateQuantitiesPayloadSchema = z.object({
  lineItemsToUpdate: z.array(
    z.object({ id: z.string(), quantity: z.number() }),
  ),
  checkoutID: z.string(),
});
export type UpdateQuantitiesPayload = z.infer<typeof UpdateQuantitiesPayloadSchema>;

export const RemoveLineItemPayloadSchema = z.object({
  checkoutID: z.string(),
  lineItemId: z.string(),
});
export type RemoveLineItemPayload = z.infer<typeof RemoveLineItemPayloadSchema>;

export const UpdateEmailPayloadSchema = z.object({
  checkoutId: z.string(),
  emamilAddress: z.string().email().optional(),
});
export type UpdateEmailPayload = z.infer<typeof UpdateEmailPayloadSchema>;

export const AddressSchema = z.object({
  address1: z.string().optional(),
  address2: z.string().nullable().optional(),
  city: z.string().optional(),
  company: z.string().nullable().optional(),
  country: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().nullable().optional(),
  province: z.string().optional(),
  zip: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  default: z.boolean().optional(),
});
export type Address = z.infer<typeof AddressSchema>;

export const UpdateShippingPayloadSchema = z.object({
  checkoutId: z.string(),
  shippingAddress: z.object({
    addressList: z.array(AddressSchema).optional(),
  }),
});
export type UpdateShippingPayload = z.infer<typeof UpdateShippingPayloadSchema>;
